/**
 * Select — dropdown with custom trigger + search.
 *
 * ## ⚠️ Pattern filtres "Tous les X" (IMPORTANT)
 *
 * Pour les dropdowns de filtre avec un état "aucun filtre" (ex. "Tous les
 * employés", "Toutes les méthodes", "Tous les postes"), **NE PAS ajouter**
 * d'option explicite `{ value: '', label: 'Tous les X' }` dans le tableau
 * `options`. Utiliser le prop `placeholder` à la place.
 *
 * Quand `allowEmpty={true}` (default), la valeur `''` affiche automatiquement
 * le `placeholder` dans le trigger et ajoute en tête de liste une option
 * invisible qui permet à l'utilisateur de clear le filtre.
 *
 * @example ✅ Correct
 * ```tsx
 * <Select
 *   value={position}                // '' quand aucun filtre
 *   onChange={setPosition}
 *   placeholder="Tous les postes"   // label affiché quand value === ''
 *   options={positions.map((p) => ({ value: p, label: p }))}
 * />
 * ```
 *
 * @example ❌ INCORRECT — erreur récurrente à éviter
 * ```tsx
 * <Select
 *   value={position}
 *   options={[
 *     { value: '', label: 'Tous les postes' },  // ❌ pas d'option explicite "Tous"
 *     ...positions.map((p) => ({ value: p, label: p })),
 *   ]}
 * />
 * ```
 *
 * Même règle pour le premier champ d'un formulaire avec une valeur initiale
 * vide : utiliser `placeholder="Sélectionner un employé"` au lieu d'une
 * option `{ value: '', label: 'Sélectionner…' }`.
 */
import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /**
   * Style du libelle, dans la liste ET dans le declencheur quand l'option est
   * choisie — par exemple, afficher le nom d'une police dans cette police.
   */
  labelStyle?: CSSProperties;
  /** Element pose a droite du libelle dans la liste (un `Badge`, le plus souvent). */
  suffix?: ReactNode;
}

export interface SelectOptionGroup {
  label: string;
  options: SelectOption[];
}

export interface TreeSelectOption {
  value: string;
  label: string;
  /** Depth level for indentation (0 = root) */
  depth: number;
  disabled?: boolean;
}

export interface SelectProps {
  value?: string;
  onChange?: (value: string) => void;
  label?: string;
  error?: string;
  helperText?: string;
  /** Flat list of options */
  options?: SelectOption[];
  /** Grouped options with optgroup */
  groups?: SelectOptionGroup[];
  /** Tree/hierarchical options with indentation */
  treeOptions?: TreeSelectOption[];
  placeholder?: string;
  /** Allow selecting empty/placeholder value */
  allowEmpty?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
  /** `sm` : version compacte (32 px de haut, texte xs) pour les panneaux de proprietes — meme echelle que `Input`. */
  size?: 'sm' | 'md';
  /**
   * Zone fixe sous les options, qui ne defile pas avec elles — typiquement un
   * lien « Ajouter… » vers l'endroit ou l'on cree ce qui manque a la liste.
   * Un clic dedans referme la liste.
   */
  footer?: ReactNode;
}

/** Hauteur reservee au pied de liste dans le calcul de position (une ligne + bordure). */
const FOOTER_HEIGHT = 40;

/** Option aplatie (groupes et arbre compris), pour la navigation au clavier. */
interface FlatOption {
  value: string;
  label: string;
  disabled?: boolean;
  depth?: number;
  isGroupLabel?: boolean;
  labelStyle?: CSSProperties;
  suffix?: ReactNode;
}

function Select({
  value = '',
  onChange,
  label,
  error,
  helperText,
  options,
  groups,
  treeOptions,
  placeholder = 'Sélectionner...',
  allowEmpty = true,
  disabled = false,
  className,
  id,
  name,
  size = 'md',
  footer,
}: SelectProps) {
  const isSmall = size === 'sm';
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectId = id || label?.toLowerCase().replace(/\s+/g, '-');

  // Flatten all options for keyboard navigation
  const flattenedOptions = useMemo(() => {
    const result: FlatOption[] = [];

    if (allowEmpty && placeholder) {
      result.push({ value: '', label: placeholder, disabled: false });
    }

    if (treeOptions) {
      treeOptions.forEach((opt) => {
        result.push({ value: opt.value, label: opt.label, disabled: opt.disabled, depth: opt.depth });
      });
    } else if (groups) {
      groups.forEach((group) => {
        result.push({ value: `__group__${group.label}`, label: group.label, disabled: true, isGroupLabel: true });
        group.options.forEach((opt) => {
          result.push({ value: opt.value, label: opt.label, disabled: opt.disabled, labelStyle: opt.labelStyle, suffix: opt.suffix });
        });
      });
    } else if (options) {
      options.forEach((opt) => {
        result.push({ value: opt.value, label: opt.label, disabled: opt.disabled, labelStyle: opt.labelStyle, suffix: opt.suffix });
      });
    }

    return result;
  }, [options, groups, treeOptions, allowEmpty, placeholder]);

  // Get the selected option label and check if it's a placeholder
  const { selectedLabel, selectedLabelStyle, isPlaceholder } = useMemo(() => {
    // Look for option with matching value (including empty string)
    const found = flattenedOptions.find((opt) => opt.value === value && !opt.isGroupLabel);
    if (found) return { selectedLabel: found.label, selectedLabelStyle: found.labelStyle, isPlaceholder: false };
    // Fallback to placeholder only if no option matches
    return { selectedLabel: placeholder, selectedLabelStyle: undefined, isPlaceholder: true };
  }, [value, flattenedOptions, placeholder]);

  // Calculate dropdown position
  const updateDropdownPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = Math.min(flattenedOptions.length * 36 + 8 + (footer ? FOOTER_HEIGHT : 0), 280);

    // Decide whether to show above or below
    const showAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

    setDropdownPosition({
      top: showAbove ? rect.top - dropdownHeight - 4 + window.scrollY : rect.bottom + 4 + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
  }, [flattenedOptions.length, footer]);

  // Open dropdown
  const openDropdown = useCallback(() => {
    if (disabled) return;
    updateDropdownPosition();
    setIsOpen(true);
    // Set highlighted index to current value
    const currentIndex = flattenedOptions.findIndex((opt) => opt.value === value && !opt.isGroupLabel);
    setHighlightedIndex(currentIndex >= 0 ? currentIndex : 0);
  }, [disabled, updateDropdownPosition, flattenedOptions, value]);

  // Close dropdown
  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, []);

  // Handle option selection
  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange?.(optionValue);
      closeDropdown();
      triggerRef.current?.focus();
    },
    [onChange, closeDropdown]
  );

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, closeDropdown]);

  // Update position on scroll/resize
  useEffect(() => {
    if (!isOpen) return;

    const handlePositionUpdate = () => updateDropdownPosition();
    window.addEventListener('scroll', handlePositionUpdate, true);
    window.addEventListener('resize', handlePositionUpdate);

    return () => {
      window.removeEventListener('scroll', handlePositionUpdate, true);
      window.removeEventListener('resize', handlePositionUpdate);
    };
  }, [isOpen, updateDropdownPosition]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (disabled) return;

      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (isOpen) {
            if (highlightedIndex >= 0) {
              const option = flattenedOptions[highlightedIndex];
              if (option && !option.disabled && !option.isGroupLabel) {
                handleSelect(option.value);
              }
            }
          } else {
            openDropdown();
          }
          break;
        case 'Escape':
          event.preventDefault();
          closeDropdown();
          break;
        case 'ArrowDown':
          event.preventDefault();
          if (!isOpen) {
            openDropdown();
          } else {
            setHighlightedIndex((prev) => {
              let next = prev + 1;
              while (next < flattenedOptions.length) {
                const opt = flattenedOptions[next];
                if (!opt.disabled && !opt.isGroupLabel) return next;
                next++;
              }
              return prev;
            });
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (isOpen) {
            setHighlightedIndex((prev) => {
              let next = prev - 1;
              while (next >= 0) {
                const opt = flattenedOptions[next];
                if (!opt.disabled && !opt.isGroupLabel) return next;
                next--;
              }
              return prev;
            });
          }
          break;
        case 'Home':
          event.preventDefault();
          if (isOpen) {
            const firstSelectableIndex = flattenedOptions.findIndex((opt) => !opt.disabled && !opt.isGroupLabel);
            if (firstSelectableIndex >= 0) setHighlightedIndex(firstSelectableIndex);
          }
          break;
        case 'End':
          event.preventDefault();
          if (isOpen) {
            for (let i = flattenedOptions.length - 1; i >= 0; i--) {
              if (!flattenedOptions[i].disabled && !flattenedOptions[i].isGroupLabel) {
                setHighlightedIndex(i);
                break;
              }
            }
          }
          break;
      }
    },
    [disabled, isOpen, highlightedIndex, flattenedOptions, handleSelect, openDropdown, closeDropdown]
  );

  // Scroll highlighted option into view
  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && dropdownRef.current) {
      const options = dropdownRef.current.querySelectorAll('[data-option]');
      const highlightedOption = options[highlightedIndex] as HTMLElement;
      if (highlightedOption) {
        highlightedOption.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [isOpen, highlightedIndex]);

  const renderOption = (option: FlatOption, index: number) => {
    if (option.isGroupLabel) {
      return (
        <div
          key={option.value}
          className="px-3 py-2 text-xs font-semibold text-neutral-500 uppercase tracking-wide bg-neutral-50"
          data-option
        >
          {option.label}
        </div>
      );
    }

    const isSelected = option.value === value;
    const isHighlighted = index === highlightedIndex;
    const indentation = option.depth ? option.depth * 16 : 0;

    return (
      <button
        key={option.value}
        type="button"
        data-option
        disabled={option.disabled}
        onClick={() => !option.disabled && handleSelect(option.value)}
        onMouseEnter={() => !option.disabled && setHighlightedIndex(index)}
        className={cn(
          'w-full flex items-center gap-2 px-3 text-left transition-colors',
          isSmall ? 'py-1.5 text-xs' : 'py-2 text-sm',
          'focus:outline-none',
          option.disabled && 'text-neutral-400 cursor-not-allowed',
          !option.disabled && isHighlighted && 'bg-primary-50 text-primary-600',
          !option.disabled && !isHighlighted && 'text-dark-500 hover:bg-neutral-50',
          isSelected && !isHighlighted && 'bg-primary-50/50'
        )}
        style={{ paddingLeft: `${12 + indentation}px` }}
      >
        {option.depth !== undefined && option.depth > 0 && (
          <span className="text-neutral-400">└</span>
        )}
        <span className="flex-1 truncate" style={option.labelStyle}>{option.label}</span>
        {option.suffix && <span className="flex-shrink-0">{option.suffix}</span>}
        {isSelected && <Check className="w-4 h-4 text-primary-500 flex-shrink-0" />}
      </button>
    );
  };

  return (
    <div ref={containerRef} className={cn('w-full', className)}>
      {label && (
        <label
          htmlFor={selectId}
          className="block text-sm font-medium text-dark-500 mb-1.5"
        >
          {label}
        </label>
      )}

      {/* Hidden native select for form compatibility */}
      <input type="hidden" name={name} value={value} />

      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        id={selectId}
        onClick={() => (isOpen ? closeDropdown() : openDropdown())}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? `${selectId}-error` : helperText ? `${selectId}-helper` : undefined}
        className={cn(
          'flex w-full items-center justify-between rounded-lg border bg-white transition-colors',
          isSmall ? 'h-8 px-2 text-xs' : 'h-10 px-3 text-sm',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:border-primary-500',
          'disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400',
          error
            ? 'border-danger-500 focus-visible:ring-danger-500 focus-visible:border-danger-500'
            : 'border-neutral-300 hover:border-neutral-400',
          isOpen && 'border-primary-500 ring-2 ring-primary-500/20'
        )}
      >
        <span className={cn('truncate', isPlaceholder && 'text-neutral-400')} style={selectedLabelStyle}>
          {selectedLabel}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-neutral-500 transition-transform flex-shrink-0',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Dropdown portal */}
      {isOpen &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownRef}
            className={cn(
              'fixed flex max-h-[280px] flex-col rounded-lg border border-neutral-200 bg-white shadow-lg',
              'animate-in fade-in-0 zoom-in-95 duration-100'
            )}
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              zIndex: 99999,
            }}
          >
            <div role="listbox" className="min-h-0 flex-1 overflow-y-auto py-1">
              {flattenedOptions.map((option, index) => renderOption(option, index))}
            </div>
            {footer && (
              <div className="shrink-0 border-t border-neutral-100" onClick={closeDropdown}>
                {footer}
              </div>
            )}
          </div>,
          document.body
        )}

      {error && (
        <p id={`${selectId}-error`} className="mt-1.5 text-sm text-danger-500">
          {error}
        </p>
      )}
      {helperText && !error && (
        <p id={`${selectId}-helper`} className="mt-1.5 text-sm text-neutral-500">
          {helperText}
        </p>
      )}
    </div>
  );
}

Select.displayName = 'Select';

export { Select };
